"use client";

import { useMemo } from "react";
import type { CSSProperties } from "react";
import { SOCIAL_STATUS, SocialAccountDto, SocialName } from "@cede/types";
import { SOCIAL_NETWORK } from "@cede/types";
import { ADMIN_ADDRESSES } from "@cede/utils";
import { DiscordStandardIcon, XStandardIcon, TelegramStandardIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useHubUser, useDevice } from "@/hooks";
import { cn } from "@/lib/utils";
import { SOCIALS } from "@/utils/social";
import { ExternalLink, LogOut } from "lucide-react";

// Map social network IDs to icon components
const SOCIAL_ICON_MAP = {
  [SOCIAL_NETWORK.DISCORD]: DiscordStandardIcon,
  [SOCIAL_NETWORK.X]: XStandardIcon,
  [SOCIAL_NETWORK.TELEGRAM]: TelegramStandardIcon,
  [SOCIAL_NETWORK.NATIVE_TELEGRAM]: TelegramStandardIcon,
} as const;

type ProfileSocialsListProps = {
  onLink: (social: SocialName) => void;
  onRemove: (socialAccount: SocialAccountDto) => void;
  removeSocialAccountLoading: (socialId: number) => boolean;
  addSocialAccountLoading: (socialName: SocialName) => boolean;
  socialAccounts: SocialAccountDto[];
  isLoading: boolean;
  title?: string | null;
  unstyled?: boolean;
  listClassName?: string;
  className?: string;
  style?: CSSProperties;
};

export function ProfileSocialsList({
  onLink,
  onRemove,
  removeSocialAccountLoading,
  addSocialAccountLoading,
  socialAccounts,
  isLoading,
  title = "Social Accounts",
  unstyled = false,
  listClassName,
  className,
  style,
}: ProfileSocialsListProps) {
  const { isTMA } = useTelegramContext();
  const { hubUser } = useHubUser();
  const { isMobile } = useDevice();

  // Check if current user is an admin
  const isAdmin = useMemo(() => {
    return hubUser?.defiAddress && ADMIN_ADDRESSES.includes(hubUser.defiAddress);
  }, [hubUser?.defiAddress]);

  // For TMA, we only show the native Telegram social account (native connects automatically in tma)
  // For non-TMA, we show all social accounts except native Telegram (as there's regular Telegram account linking)
  const socialsList = useMemo(() => {
    return SOCIALS.filter((social) =>
      isTMA ? social.id === SOCIAL_NETWORK.NATIVE_TELEGRAM : social.id !== SOCIAL_NETWORK.NATIVE_TELEGRAM,
    );
  }, [isTMA]);

  const shouldStyle = !unstyled;
  const showBackground = shouldStyle && !isMobile;
  const padding = shouldStyle ? (isMobile ? "none" : "md") : "none";

  return (
    <InfoCard
      title={title || undefined}
      padding={padding}
      withBackground={showBackground}
      withBorder={showBackground}
      gap={unstyled ? "none" : title ? "md" : "sm"}
      className={cn(
        "flex w-full flex-col overflow-hidden",
        shouldStyle && isMobile ? "px-0 py-4" : "",
        !shouldStyle && "p-0 rounded-none",
        className,
      )}
      contentClassName="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
      style={style}
    >
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto pr-3 scrollbar-visible",
          unstyled && "pr-0",
          listClassName,
        )}
      >
        {socialsList.map((social, index) => {
          const userSocial = socialAccounts.find((sa) => sa.socialName === social.id);
          const isRemoveLoading = userSocial ? removeSocialAccountLoading(userSocial.id) || isLoading : false;
          const isAddLoading = addSocialAccountLoading(social.id) || isLoading;
          const isLinked = userSocial && userSocial.status === SOCIAL_STATUS.ONLINE;
          const needsReconnect = userSocial?.status === SOCIAL_STATUS.OFFLINE;
          const showActions = !social.autoLink || isAdmin;

          const IconComponent = SOCIAL_ICON_MAP[social.id as keyof typeof SOCIAL_ICON_MAP];

          return (
            <div
              key={social.id}
              className={cn(
                "flex items-center justify-between py-3 md:py-4",
                index < socialsList.length - 1 && "border-b border-cede-border-container/70",
              )}
            >
              {/* Social Info */}
              <div className="flex min-w-0 items-center gap-3">
                {IconComponent ? (
                  <IconComponent size="md" withUnderlay underlayClassName="size-10" variant="primary" />
                ) : (
                  <span className="flex size-10 items-center justify-center rounded-full bg-cede-primary/10 text-cede-primary">
                    {social.icon}
                  </span>
                )}
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium text-cede-text-primary">{social.name}</span>
                  {userSocial && (
                    <span className="truncate text-sm text-cede-text-secondary">
                      {social.usernamePrefix}
                      {userSocial.username}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {showActions && (
                <div className="flex gap-2">
                  {isLinked ? (
                    <Button
                      variant="unlink"
                      size="small"
                      onClick={() => userSocial && onRemove(userSocial)}
                      disabled={isRemoveLoading}
                      loading={isRemoveLoading}
                      leftIcon={<LogOut className="h-4 w-4" />}
                    >
                      {isRemoveLoading ? "Unlinking..." : "Unlink"}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => onLink(social.id)}
                      disabled={isAddLoading}
                      loading={isAddLoading}
                      rightIcon={<ExternalLink className="h-4 w-4" />}
                    >
                      {needsReconnect ? "Reconnect" : "Link"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </InfoCard>
  );
}
